<template>
  <v-card class="new-member-container">
    <v-card-title>Editando usuario</v-card-title>
    <v-card-text>
      <template v-if="!editingMember">
        <v-skeleton-loader type="paragraph" />
      </template>
      <template v-else>
        <v-row>
          <v-col cols="12" md="4">
            <v-form @submit.prevent v-model="valid">
              <h3 class="mb-4">Datos</h3>
              <v-text-field
                label="Email de la cuenta"
                v-model="email"
                :rules="$rule('required|email')"
                :persistent-hint="!!props.member"
                :disabled="!!props.member"
                @input="emailChanged"
                hint="Ingrese el mail con el que se registró el usuario"
                outlined
                dense
              >
                <template v-slot:append>
                  <v-fade-transition leave-absolute>
                    <v-progress-circular
                      v-if="loadingUser"
                      size="24"
                      color="info"
                      indeterminate
                    ></v-progress-circular>
                    <v-icon v-else-if="!props.member && user" color="green"
                      >mdi-check</v-icon
                    >
                    <v-icon
                      v-else-if="!props.member && userNotFound"
                      color="red"
                      >clear</v-icon
                    >
                  </v-fade-transition>
                </template>
              </v-text-field>
              <v-text-field
                label="Nombre"
                v-if="user"
                v-model="user.name"
                disabled
                outlined
                dense
              ></v-text-field>
              <v-text-field
                label="Descripción"
                v-model="editingMember.description"
                hint="Opcional. Descripción del usuario"
                outlined
                dense
              ></v-text-field>
              <v-select
                label="Tipo de usuario"
                class=""
                :disabled="!!props.member && props.member.type === 'owner'"
                :items="memberTypes"
                v-model="editingMember.type"
                :menu-props="{ offsetY: true }"
                hide-details
                @change="typeChanged"
                outlined
                dense
              ></v-select>
              <template v-if="!props.member || props.member.type !== 'owner'">
                <h4 class="mb-0 mt-4">Sucursales</h4>
                <v-radio-group
                  class="mt-0 mb-2"
                  v-if="role.allBranches"
                  hide-details
                  v-model="editingMember.allBranches"
                >
                  <v-radio label="Todas" :value="true"></v-radio>
                  <v-radio label="Algunas" :value="false"></v-radio>
                </v-radio-group>
                <template v-if="!editingMember.allBranches">
                  <v-select
                    label="Sucursales"
                    class="mb-2"
                    :disabled="!!props.member && props.member.type === 'owner'"
                    :items="props.branches"
                    return-object
                    hide-details
                    item-text="name"
                    v-model="editingMember.branches"
                    :menu-props="{ offsetY: true }"
                    chips
                    small-chips
                    multiple
                    outlined
                    dense
                  ></v-select>
                </template>
                <h4 class="mb-0 mt-4">Cajas</h4>
                <v-radio-group
                  class="mt-0 mb-2"
                  v-if="editingMember.allCajas !== undefined"
                  v-model="editingMember.allCajas"
                  hide-details
                >
                  <v-radio label="Todas" :value="true"></v-radio>
                  <v-radio label="Algunas" :value="false"></v-radio>
                </v-radio-group>
                <template v-if="!editingMember.allCajas">
                  <v-select
                    label="Cajas"
                    :items="availableCajas"
                    item-text="name"
                    item-value="id"
                    v-model="editingMember.cajaIds"
                    :menu-props="{ offsetY: true }"
                    chips
                    small-chips
                    multiple
                    outlined
                    dense
                  ></v-select>
                </template>
              </template>
            </v-form>
          </v-col>
          <v-col cols="12" md="8">
            <h3 class="mb-4">Permisos</h3>
            <div
              class="permission-group"
              v-for="group of permissionGroups"
              :key="group.key"
            >
              <h4>{{ $t(group.title) }}</h4>
              <v-switch
                v-for="perm of group.items"
                :key="perm.key"
                :input-value="permEnabled(perm.id)"
                :disabled="!!props.member && props.member.type === 'owner'"
                @change="togglePerm(perm.id)"
                class="input--label-left my-1"
                dense
                hide-details
                :label="$t(perm.title)"
              />
            </div>
          </v-col>
        </v-row>
      </template>
    </v-card-text>
    <v-card-actions class="justify-end">
      <v-btn
        text
        v-if="props.member && props.member.type !== 'owner'"
        @click="remove"
        color="red"
        ><v-icon>mdi-delete</v-icon> {{ $t('delete') }}</v-btn
      >
      <v-btn text @click="cancel">{{ $t('cancel') }}</v-btn>
      <v-btn color="primary" @click="save" :disabled="loading || !valid">{{
        $t('save')
      }}</v-btn>
    </v-card-actions>
  </v-card>
</template>

<script setup lang="ts">
  import { ref, onMounted, computed, getCurrentInstance, watch } from 'vue';
  import { IMember, MemberRole } from '../../interfaces/IMember';
  import { PermissionGroups } from '../../modules/PermissionData';
  import { PermissionType } from '../../enums/PermissionType';
  import RestaurantsService from '../../api/services/restaurants.service';
  import UsersService from '../../api/services/users.service';
  import { IUser } from '../../interfaces/IUser';
  import { debounce } from '../../utils';
  import { vueData } from '../../VueData';
  import { AlertType } from '../../interfaces/IAlert';
  import { IRestaurant } from '../../interfaces/IRestaurant';
  import ConfirmModal from './ConfirmModal.vue';
  import { IBranch } from '../../interfaces/IBranch';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { ICaja } from '../../interfaces/ICaja';
  import CajasService from '../../api/services/cajas.service';

  const props = defineProps<{
    restaurant: IRestaurant;
    branches: IBranch[];
    member?: IMember;
  }>();

  const emit = defineEmits(['close']);

  // vue-js-modal dynamic modals deliver result events bound via the
  // $modal.show events argument on the WRAPPER component, so they must be
  // emitted through $parent (see ModalsStockItem.vue) — a plain emit() here
  // never reaches the opener's listeners.
  const proxy = getCurrentInstance()!.proxy;

  // Services
  const restaurantsService = new RestaurantsService();
  const usersService = new UsersService();

  const modals = useModal();
  const ui = useUi();

  // Reactive state
  const editingMember = ref<IMember | null>(null);
  const user = ref<IUser | null>(null);
  const role = ref<IMember | null>(null);
  const email = ref<string | null>(null);
  const loading = ref(false);
  const loadingUser = ref(false);
  const userNotFound = ref(false);
  const valid = ref(false);
  const availableCajas = ref<ICaja[]>([]);

  // Computed values
  const memberTypes = computed(() => {
    const types = [
      { value: MemberRole.Owner, text: 'Dueño' },
      { value: MemberRole.Admin, text: 'Administrador' },
      { value: MemberRole.Custom, text: 'Personalizado' },
    ];

    if (props.member || !props.member) {
      types.splice(0, 1);
    }

    return types;
  });

  // Constants
  const permissionGroups = PermissionGroups;
  const defaultAdmin = computed(() => {
    let value = 0;
    for (let item in PermissionType) {
      if (isNaN(Number(item))) {
        // @ts-ignore
        value |= PermissionType[item];
      }
    }
    return value;
  });
  const defaultCustom = 0;

  // Methods
  const emailChanged = debounce(async () => {
    loadingUser.value = true;
    try {
      user.value = null;
      if (editingMember.value) editingMember.value.userId = null;
      user.value = await usersService.getByEmail(email.value);
      userNotFound.value = !user.value;
      if (user.value && editingMember.value) {
        editingMember.value.userId = user.value.id;
      }
    } catch (e) {
      console.error(e);
      vueData.serverError(e);
    } finally {
      loadingUser.value = false;
    }
  }, 500);

  const typeChanged = () => {
    if (!editingMember.value) return;

    switch (editingMember.value.type) {
      case MemberRole.Admin:
      case MemberRole.Owner:
        editingMember.value.permissions = defaultAdmin.value;
        break;
      case MemberRole.Custom:
        editingMember.value.permissions = defaultCustom;
        break;
    }
  };

  const remove = async () => {
    if (!user.value) return;

    // Using Vue's global modal system
    // Note: Implementation might need to be adjusted based on your modal system
    modals.show(
      ConfirmModal,
      {
        //title: proxy.$t('delete.title', { name: user.value.name }),
        //message: proxy.$t('delete.warning'),
        title: `Eliminar usuario ${user.value.name}`,
        message: '¿Está seguro que desea eliminar este usuario?',
      },
      {
        name: 'delete',
        adaptive: true,
        height: 'auto',
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 600,
        clickToClose: false,
      },
      {
        confirm: async () => {
          try {
            await restaurantsService.deleteMember(
              props.restaurant.id,
              user.value!.id
            );
            modals.hide('delete');
            proxy.$parent?.$emit('deleted');
          } catch (e) {
            console.error(e);
            vueData.serverError(e);
          }
        },
      }
    );
  };

  const cancel = () => {
    emit('close');
  };

  const loadCajas = async () => {
    if (!editingMember.value) return;
    if (editingMember.value.allCajas) {
      availableCajas.value = [];
      return;
    }
    const branchIds = editingMember.value.allBranches
      ? props.branches.map((b) => b.id)
      : editingMember.value.branches.map((b) => b.id);

    const seen = new Set<number>();
    const cajas: ICaja[] = [];
    for (const bId of branchIds) {
      try {
        const c = await CajasService.getAll(props.restaurant.id, bId);
        for (const caja of c) {
          if (!seen.has(caja.id)) {
            seen.add(caja.id);
            cajas.push(caja);
          }
        }
      } catch (e) {
        console.error(e);
      }
    }
    availableCajas.value = cajas;
  };

  watch(
    () => [
      editingMember.value?.allBranches,
      editingMember.value?.branches,
      editingMember.value?.allCajas,
    ],
    () => {
      loadCajas();
    },
    { deep: true }
  );

  const save = async () => {
    if (!user.value) {
      vueData.addAlert(
        'Debe ingresar el email de un usuario existente',
        AlertType.Error
      );
      return;
    }

    loading.value = true;
    try {
      if (!editingMember.value) return;

      let member = { ...editingMember.value } as any;
      // @ts-ignore
      member.branches = member.branches.map((value) => value.id);
      if (member.allCajas) {
        member.cajaIds = [];
      }
      member = await restaurantsService.saveMember(props.restaurant.id, member);
      ui.showToast(AlertType.Success, 'Usuario guardado');
      proxy.$parent?.$emit('saved', member);
      emit('close');
    } catch (e: any) {
      ui.serverError(e, 'Error al guardar el usuario, intente nuevamente');
    } finally {
      loading.value = false;
    }
  };

  const togglePerm = (id: number) => {
    if (!editingMember.value) return;

    editingMember.value.permissions ^= id;

    if (editingMember.value.permissions === defaultAdmin.value) {
      editingMember.value.type = MemberRole.Admin;
    } else {
      editingMember.value.type = MemberRole.Custom;
    }
  };

  const permEnabled = (id: number) => {
    return (
      editingMember.value?.permissions !== null &&
      (editingMember.value!.permissions & id) === id
    );
  };

  // Lifecycle hooks
  onMounted(() => {
    role.value = vueData.role;

    if (props.member) {
      editingMember.value = { ...props.member };
      editingMember.value.permissions = props.member.permissions;
      user.value = props.member.user;
      email.value = props.member.user.email;
    } else {
      editingMember.value = {
        restaurantId: props.restaurant.id,
        type: MemberRole.Admin,
        description: null,
        permissions: 0,
        allBranches: role.value?.allBranches,
        branches: [],
        allCajas: true,
        cajaIds: [],
      } as IMember;
    }

    if (!props.member || !props.member.permissions) typeChanged();
    loadCajas();
  });
</script>
